import React from "react"
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete"
import Chip from "@mui/material/Chip"
import TextField from "@mui/material/TextField"
import { Checkbox, SxProps } from "@mui/material"
import styles from "./muiAutoComplete.module.scss"

type Option = {
    title: string
    id: string | number
    search_key?: string
}

type Props = {
    options: Option[]
    value?: Option | Option[] | null
    onChange?: (value: Option | Option[] | null) => void
    label?: string
    placeholder?: string
    disabled?: boolean
    error?: boolean
    helperText?: string
    required?: boolean
    fullWidth?: boolean
    size?: "small" | "medium"
    disablePortal?: boolean
    containerHeight?: string
    renderOption?: (
        props: React.HTMLAttributes<HTMLLIElement>,
        option: Option,
        { selected }: { selected: boolean }
    ) => React.ReactNode
    multiple?: boolean
    sx?: SxProps
    disableClearable?: boolean
}

const MuiAutoComplete: React.FC<Props> = ({
    options,
    value,
    onChange,
    label,
    placeholder,
    disablePortal = true,
    disabled = false,
    error = false,
    helperText,
    required = false,
    fullWidth = false,
    containerHeight,
    size = "medium",
    multiple = false,
    sx = {},
    renderOption,
    disableClearable = false,
}) => {
    const filterOptions = createFilterOptions<Option>({
        stringify: option => `${option.title} ${option.search_key ?? ""}`,
    })

    return (
        <Autocomplete
            multiple={multiple}
            disablePortal={disablePortal}
            disableCloseOnSelect={multiple}
            options={options}
            value={value}
            disableClearable={disableClearable}
            onChange={(_, newValue) => onChange?.(newValue as Option)}
            getOptionLabel={(option: Option) => option.title}
            renderOption={
                renderOption
                    ? renderOption
                    : multiple
                      ? (props: any, option: any, { selected }: { selected: boolean }) => {
                            const { key, ...optionProps } = props
                            return (
                                <li key={key} {...optionProps} className={styles.checkboxContainer}>
                                    <Checkbox
                                        sx={{
                                            '&.Mui-checked': {
                                                color: '#101941',
                                            },
                                        }}
                                    size="small" className={styles.checkbox} style={{ marginRight: 8 }} checked={selected} />
                                    {option.title}
                                </li>
                            )
                        }
                      : (props, option) => (
                            <li {...props} key={option.id}>
                                {option.title}
                            </li>
                        )
            }
            isOptionEqualToValue={(option, value) => option.id === value.id}
            disabled={disabled}
            fullWidth={fullWidth}
            filterOptions={filterOptions}
            onClick={() => {}}
            limitTags={multiple ? 1 : undefined}
            size={size}
            renderValue={
                multiple
                    ? (value: Option | Option[], getItemProps) => {
                          if (Array.isArray(value)) {
                              return value.map((option: Option, index: number) => {
                                  const { ...itemProps } = getItemProps({ index })
                                  return (
                                      <Chip
                                          variant="autocomplete"
                                          label={option.title}
                                          key={option.id}
                                          {...itemProps}
                                          onClick={() => {}}
                                      />
                                  )
                              })
                          }
                      }
                    : undefined
            }
            sx={{
                ...(error
                    ? {
                          "&.MuiAutocomplete-root:before, & .MuiAutocomplete-root:after": {
                              borderColor: "#F94054 !important", // MUI error color
                          },
                          "&.MuiAutocomplete-root": {
                              border: "1px solid #F94054 !important",
                              height: containerHeight ? containerHeight : "auto",
                          },
                      }
                    : {
                          "&.MuiAutocomplete-root": {
                              height: containerHeight ? containerHeight : "auto",
                          },
                      }),
                "& .MuiInput-root:before, & .MuiInput-root:after": {
                    borderBottom: disabled ? "none !important" : undefined,
                },
                "& .MuiFormControl-root": {
                    height: "100%",
                },
                ...(disabled ? {
                    cursor: "not-allowed",
                    '& .MuiInputBase-root,& .MuiInputBase-input': {
                        cursor: "not-allowed",
                    },
                } : {}),
                ...sx,
            }}
            renderInput={params => (
                <TextField
                    {...params}
                    label={label}
                    placeholder={placeholder}
                    helperText={helperText}
                    required={required}
                    variant="standard"
                />
            )}
        />
    )
}

export default MuiAutoComplete
